import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { CreditCard, Plus } from "lucide-react";

import {
  Box,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Chip,
} from "@mui/material";

import DataTable from "@/components/mui/DataTable";

export default function Cashier({ user }) {
  const [cashiers, setCashiers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showOpenModal, setShowOpenModal] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);

  const [selectedCashier, setSelectedCashier] = useState(null);
  const [openingAmount, setOpeningAmount] = useState(0);
  const [closingAmount, setClosingAmount] = useState(0);

  const [pagination, setPagination] = useState({
    page: 1, // 1-based (Laravel)
    perPage: 15,
    total: 0,
    lastPage: 0,
  });

  useEffect(() => {
    fetchCashiers(1, pagination.perPage);
  }, []);

  const fetchCashiers = async (page = 1, perPage = pagination.perPage) => {
    try {
      setLoading(true);

      const response = await axios.get(`${API}/cashiers`, {
        params: { page, per_page: perPage },
      });

      const data = response.data;

      if (Array.isArray(data)) {
        // Caso a API não esteja paginada
        setCashiers(data);
        setPagination({
          page: 1,
          perPage: data.length,
          total: data.length,
          lastPage: 1,
        });
      } else {
        // Formato paginate() do Laravel
        setCashiers(data.data || []);
        setPagination({
          page: data.current_page,
          perPage: data.per_page,
          total: data.total,
          lastPage: data.last_page,
        });
      }
    } catch (error) {
      toast.error("Erro ao carregar caixas");
    } finally {
      setLoading(false);
    }
  };

  const formatMoney = (value) =>
    `R$ ${Number(value || 0).toFixed(2).replace(".", ",")}`;

  const handleOpenCashier = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/cashiers/open`, {
        opening_amount: parseFloat(openingAmount) || 0,
        company_id: user.company_id,
      });
      toast.success("Caixa aberto com sucesso!");
      setShowOpenModal(false);
      setOpeningAmount(0);
      fetchCashiers(pagination.page, pagination.perPage);
    } catch (error) {
      toast.error(error.response?.data?.message || "Erro ao abrir caixa");
    }
  };

  const handleCloseCashier = async (e) => {
    e.preventDefault();
    if (!selectedCashier) return;

    try {
      await axios.post(`${API}/cashiers/${selectedCashier.id}/close`, {
        closing_amount: parseFloat(closingAmount) || 0,
        company_id: user.company_id,
      });
      toast.success("Caixa fechado com sucesso!");
      setShowCloseModal(false);
      setClosingAmount(0);
      setSelectedCashier(null);
      fetchCashiers(pagination.page, pagination.perPage);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao fechar caixa");
    }
  };

  const openCloseModal = (cashier) => {
    setSelectedCashier(cashier);
    setClosingAmount(cashier.opening_amount);
    setShowCloseModal(true);
  };

  const handleChangePage = (event, newPageZeroBased) => {
    const newPage = newPageZeroBased + 1; // TablePagination é 0-based
    fetchCashiers(newPage, pagination.perPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newPerPage = parseInt(event.target.value, 10);
    setPagination((prev) => ({
      ...prev,
      perPage: newPerPage,
      page: 1,
    }));
    fetchCashiers(1, newPerPage);
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: 200,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const showDifference =
    selectedCashier &&
    closingAmount !== null &&
    closingAmount !== undefined &&
    Number(closingAmount) !== Number(selectedCashier.opening_amount);

  const difference =
    selectedCashier && closingAmount !== null && closingAmount !== undefined
      ? Number(closingAmount) - Number(selectedCashier.opening_amount)
      : 0;

  // ========= Definição das colunas para o DataTable =========
  const columns = [
    {
      id: "operator",
      label: "Operador",
      render: (cashier) => (
        <Typography fontWeight={600}>
          {cashier.user?.name}
        </Typography>
      ),
    },
    {
      id: "opened_at",
      label: "Abertura",
      render: (cashier) =>
        cashier.opened_at
          ? new Date(cashier.opened_at).toLocaleString("pt-BR")
          : "-",
    },
    {
      id: "closed_at",
      label: "Fechamento",
      render: (cashier) =>
        cashier.closed_at
          ? new Date(cashier.closed_at).toLocaleString("pt-BR")
          : "-",
    },
    {
      id: "opening_amount",
      label: "Valor Inicial",
      align: "right",
      render: (cashier) => formatMoney(cashier.opening_amount),
    },
    {
      id: "closing_amount",
      label: "Valor Final",
      align: "right",
      render: (cashier) =>
        cashier.closing_amount !== null &&
          cashier.closing_amount !== undefined
          ? formatMoney(cashier.closing_amount)
          : "-",
    },
    {
      id: "status",
      label: "Status",
      render: (cashier) => (
        <Chip
          size="small"
          color={cashier.status === "open" ? "success" : "info"}
          label={cashier.status === "open" ? "Aberto" : "Fechado"}
        />
      ),
    },
    {
      id: "actions",
      label: "Ações",
      render: (cashier) =>
        cashier.status === "open" ? (
          <Button
            variant="contained"
            color="error"
            size="small"
            onClick={() => openCloseModal(cashier)}
            data-testid={`close-cashier-${cashier.id}`}
          >
            Fechar Caixa
          </Button>
        ) : null,
    },
  ];

  const tablePagination = {
    page: pagination.page - 1, // 0-based para MUI
    rowsPerPage: pagination.perPage,
    total: pagination.total,
    onPageChange: handleChangePage,
    onRowsPerPageChange: handleChangeRowsPerPage,
    rowsPerPageOptions: [5, 10, 15, 25, 50],
  };

  return (
    <Box data-testid="cashier-page">
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={4}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Caixas
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Controle de caixas abertos e fechados
          </Typography>
        </Box>

        <Button
          variant="contained"
          startIcon={<Plus size={16} />}
          onClick={() => setShowOpenModal(true)}
          data-testid="open-cashier-btn"
        >
          Abrir Caixa
        </Button>
      </Box>

      <Paper className="data-table-container">
        {/* Tabela / vazio */}
        {pagination.total === 0 ? (
          <Box
            sx={{
              py: 6,
              px: 3,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              gap: 1.5,
            }}
          >
            <CreditCard size={64} />
            <Typography variant="h6">Nenhum caixa registrado</Typography>
            <Typography variant="body2">
              Abra um caixa para começar a realizar vendas
            </Typography>
          </Box>
        ) : (
          <DataTable
            columns={columns}
            rows={cashiers}
            getRowId={(row) => row.id}
            getRowTestId={(row) => `cashier-row-${row.id}`}
            pagination={tablePagination}
          />
        )}
      </Paper>

      {/* Dialog Abrir Caixa */}
      <Dialog
        open={showOpenModal}
        onClose={() => setShowOpenModal(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Abrir Caixa</DialogTitle>
        <DialogContent dividers>
          <Box component="form" onSubmit={handleOpenCashier} sx={{ mt: 1 }}>
            <TextField
              label="Valor Inicial (R$)"
              type="number"
              size="small"
              fullWidth
              inputProps={{ step: "0.01" }}
              value={openingAmount}
              onChange={(e) =>
                setOpeningAmount(parseFloat(e.target.value) || 0)
              }
              required
              data-testid="opening-amount-input"
            />

            <DialogActions sx={{ mt: 3 }}>
              <Button
                type="button"
                variant="outlined"
                onClick={() => setShowOpenModal(false)}
                data-testid="cancel-open-btn"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="contained"
                data-testid="submit-open-btn"
              >
                Abrir Caixa
              </Button>
            </DialogActions>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Dialog Fechar Caixa */}
      <Dialog
        open={showCloseModal}
        onClose={() => {
          setShowCloseModal(false);
          setSelectedCashier(null);
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Fechar Caixa</DialogTitle>
        <DialogContent dividers>
          <Box component="form" onSubmit={handleCloseCashier} sx={{ mt: 1 }}>
            {selectedCashier && (
              <Box
                sx={{
                  mb: 2,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: "grey.50",
                  fontSize: 14,
                }}
              >
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Operador:</strong>{" "}
                  {selectedCashier.user?.name}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Abertura:</strong>{" "}
                  {selectedCashier.opened_at
                    ? new Date(
                      selectedCashier.opened_at
                    ).toLocaleString("pt-BR")
                    : "-"}
                </Typography>
                <Typography variant="body2">
                  <strong>Valor Inicial:</strong>{" "}
                  {formatMoney(selectedCashier.opening_amount)}
                </Typography>
              </Box>
            )}

            <TextField
              label="Valor Final (R$)"
              type="number"
              size="small"
              fullWidth
              inputProps={{ step: "0.01" }}
              value={closingAmount}
              onChange={(e) =>
                setClosingAmount(parseFloat(e.target.value) || 0)
              }
              required
              data-testid="closing-amount-input"
            />

            {selectedCashier && showDifference && (
              <Box
                sx={{
                  mt: 2,
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: "warning.50",
                  fontSize: 13,
                }}
              >
                <Typography variant="body2">
                  <strong>Diferença:</strong>{" "}
                  <span
                    style={{
                      color:
                        difference > 0
                          ? "#10b981"
                          : difference < 0
                            ? "#ef4444"
                            : "#000",
                      fontWeight: 700,
                    }}
                  >
                    {formatMoney(difference)}
                  </span>
                </Typography>
              </Box>
            )}

            <DialogActions sx={{ mt: 3 }}>
              <Button
                type="button"
                variant="outlined"
                onClick={() => {
                  setShowCloseModal(false);
                  setSelectedCashier(null);
                }}
                data-testid="cancel-close-btn"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="error"
                data-testid="submit-close-btn"
              >
                Fechar Caixa
              </Button>
            </DialogActions>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
